import { type FamilyHistoryRecord, groupFamilyHistoryRecords } from '@liza/fhir-utils';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { FamilyConditionRow } from './FamilyConditionRow';
import { FamilyHistoryNegativesRow } from './FamilyHistoryNegativesRow';
import type { FamilyHistoryItemProps } from './familyHistoryRows';
import { FamilyHistoryUnattributedRow } from './FamilyHistoryUnattributedRow';
import { FamilyRelativeRow } from './FamilyRelativeRow';

export type { PromotionStatus } from './RecordListItem';

export type FamilyHistoryView = 'relative' | 'condition';

const isFamilyHistoryView = (value: string): value is FamilyHistoryView =>
  value === 'relative' || value === 'condition';

export type FamilyMemberHistoryListProps = FamilyHistoryItemProps & {
  /** The patient's whole family history. Grouping a partial list gives wrong merges and counts. */
  familyMemberHistories: FamilyHistoryRecord[];
  /** Which pivot opens first; the reader can switch. */
  defaultView?: FamilyHistoryView;
};

/**
 * The patient's family history with the noise imported rows carry removed, readable two ways: one
 * row per relative (parents first, merged across the resources that describe the same person) or
 * one row per condition listing the relatives who have it. Pertinent negatives are one line; history
 * recorded against no particular relative is one collapsed line. Grouping and ordering come from
 * `groupFamilyHistoryRecords`.
 */
export const FamilyMemberHistoryList = ({
  familyMemberHistories,
  defaultView = 'relative',
  ...itemProps
}: FamilyMemberHistoryListProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const [view, setView] = useState<FamilyHistoryView>(defaultView);
  const grouped = useMemo(() => groupFamilyHistoryRecords(familyMemberHistories), [familyMemberHistories]);
  const { relatives, negatives, unattributed, conditions } = grouped;
  const isEmpty = relatives.length === 0 && negatives.length === 0 && unattributed === undefined;

  if (isEmpty) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <p className="py-8 text-center text-sm text-muted-foreground">{t('noFamilyHistoryFound')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="flex justify-end px-3 pb-2">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={view}
          onValueChange={(value) => isFamilyHistoryView(value) && setView(value)}
          aria-label={t('familyHistoryView')}
          data-testid="family-history-view-toggle"
        >
          <ToggleGroupItem value="relative" className="h-7 px-2 text-xs" data-testid="family-history-view-relative">
            {t('familyHistoryViewByRelative')}
          </ToggleGroupItem>
          <ToggleGroupItem value="condition" className="h-7 px-2 text-xs" data-testid="family-history-view-condition">
            {t('familyHistoryViewByCondition')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="space-y-1">
        {view === 'relative'
          ? relatives.map((row) => <FamilyRelativeRow key={row.relative.key} row={row} {...itemProps} />)
          : conditions.map((row) => <FamilyConditionRow key={row.condition.key} row={row} {...itemProps} />)}
        {negatives.length > 0 && <FamilyHistoryNegativesRow negatives={negatives} {...itemProps} />}
        {unattributed && <FamilyHistoryUnattributedRow row={unattributed} {...itemProps} />}
      </div>
    </div>
  );
};
